<template>
  <div class="admin-login-container">
    <div class="login-card glass tech-card">
      <div class="login-header">
        <h1 class="login-title">XBT 管理端</h1>
        <p class="login-subtitle">学习通签到助手后台管理系统</p>
      </div>

      <var-form ref="formRef">
        <var-space direction="column" :size="[20, 0]">
          <var-input
            v-model="formData.username"
            placeholder="请输入用户名"
            :rules="[rules.required]"
            clearable
            variant="outlined"
          >
            <template #prepend-icon>
              <var-icon name="account-circle" />
            </template>
          </var-input>

          <var-input
            v-model="formData.password"
            type="password"
            placeholder="请输入密码"
            :rules="[rules.required]"
            clearable
            variant="outlined"
            @keyup.enter="handleLogin"
          >
            <template #prepend-icon>
              <var-icon name="lock" />
            </template>
          </var-input>

          <var-button
            block
            :loading="loading"
            :disabled="loading"
            color="var(--primary-color)"
            @click="handleLogin"
          >
            {{ loading ? '登录中...' : '登录' }}
          </var-button>
        </var-space>
      </var-form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useAdminStore } from '@/stores/AdminStore'
import adminApi from '@/utils/adminApi'
import { Snackbar } from '@varlet/ui'

const router = useRouter()
const adminStore = useAdminStore()
const formRef = ref(null)
const loading = ref(false)

const formData = reactive({
  username: '',
  password: ''
})

const rules = {
  required: (val) => !!val || '此字段为必填项'
}

const handleLogin = async () => {
  try {
    console.log('开始登录流程...')
    
    // 检查用户名和密码是否为空
    if (!formData.username || !formData.password) {
      console.warn('用户名或密码为空')
      Snackbar.warning('请输入用户名和密码')
      return
    }
    
    // 表单验证
    if (formRef.value) {
      const valid = await formRef.value.validate()
      if (!valid) {
        console.warn('表单验证失败')
        Snackbar.warning('请填写完整的登录信息')
        return
      }
    }

    loading.value = true
    console.log('发送登录请求:', { username: formData.username.trim() })
    
    const response = await adminApi.post('/login', {
      username: formData.username.trim(),
      password: formData.password
    })

    console.log('登录响应:', response)

    if (response.suc && response.data) {
      console.log('登录成功，保存token和用户信息')
      adminStore.login(response.data.token, response.data.adminInfo)
      Snackbar.success('登录成功')
      // 延迟跳转，确保提示信息显示
      setTimeout(() => {
        router.push({ name: adminStore.homeRouteName })
      }, 500)
    } else {
      console.error('登录失败:', response.msg)
      Snackbar.error(response.msg || '登录失败')
    }
  } catch (error) {
    console.error('登录异常:', error)
    console.error('错误详情:', {
      message: error.message,
      response: error.response,
      request: error.request,
      config: error.config
    })
    
    // 错误信息已经在响应拦截器中显示了，这里不需要重复显示
    // 但如果响应拦截器没有处理，这里作为兜底
    if (error.response) {
      // 响应拦截器应该已经处理了
      const status = error.response.status
      const data = error.response.data
      console.error('响应错误:', { status, data })
      // 如果响应拦截器没有显示错误，这里显示
      if (!data?.msg) {
        Snackbar.error(`登录失败 (${status})`)
      }
    } else if (error.request) {
      console.error('网络错误: 请求已发送但未收到响应')
      Snackbar.error('网络连接失败，请检查网络设置或确认后端服务是否正常运行')
    } else {
      console.error('请求配置错误:', error.message)
      Snackbar.error(error.message || '登录失败，请稍后重试')
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.admin-login-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background: radial-gradient(circle at 50% 50%, rgba(14, 165, 233, 0.05) 0%, transparent 80%);
}

.login-card {
  width: 100%;
  max-width: 400px;
  padding: 40px 32px;
  box-sizing: border-box;
}

.login-header {
  text-align: center;
  margin-bottom: 40px;
}

.login-title {
  font-size: 28px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
  background: linear-gradient(135deg, var(--primary-color), #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.login-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

:deep(.var-input) {
  margin-bottom: 0;
}

:deep(.var-space) {
  width: 100%;
}
</style>
